import { FC } from 'react';
import { useTranslation } from 'next-i18next';

import { EditorialCard } from '@phoenix/core/frontend/shared/ui/editorial-card/editorial-card';
import { formatIntlDate } from '@phoenix/core/frontend/shared/util/format/dates';
import { Button } from '@phoenix/design-system/frontend/ui/button/button';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

import { DynamicLinkWrapper } from '@phoenix/team-m/dashboard/frontend/shared/dynamic-link-wrapper';
import { useLoadMoreItems } from '@phoenix/team-m/dashboard/frontend/shared/use-load-more-items';
import { SharedHint } from '@phoenix/team-m/dashboard/frontend/shared/components/shared-hint';

export type TimberSportsEvent = {
  id: string;
  name: string;
  begin: string;
  imageUrl: string;
  location: string;
  liveUrl?: string;
  ticketUrl?: string;
};

interface TimbersportsEventsProps {
  events: Array<TimberSportsEvent>;
}

export const TimberSportsEvents: FC<TimbersportsEventsProps> = ({ events }) => {
  const { t } = useTranslation();
  const { items, onClickLoadMore, isLastPage } = useLoadMoreItems(events);

  const getTranslations = (live?: string, ticket?: string): { labelTag?: string; linkOut?: string } => {
    if (live) {
      return { labelTag: t('dashboard_cards_tag_videoDemand'), linkOut: t('dashboard_events_card_link_liveStream') };
    } else if (ticket) {
      return { labelTag: t('dashboard_cards_tag_ticketAvailable'), linkOut: t('dashboard_events_card_link_event') };
    }
    return { labelTag: undefined, linkOut: undefined };
  };

  return events?.length ? (
    <>
      <div className="mt-8 grid grid-cols-1 items-center gap-6 tablet:grid-cols-2 desktop:grid-cols-3" data-testid="timbersports-events">
        {items.map(({ id, name, liveUrl, ticketUrl, begin, location, imageUrl }: TimberSportsEvent, i) => (
          <DynamicLinkWrapper
            key={`dynamicLinkTimberSports-${id}`}
            hyperLink={liveUrl ?? ticketUrl}
            className="flex h-full"
            allowReferrer={true}
          >
            <EditorialCard
              key={`timbersports-${id}`}
              dataTestId={`timbersports-${i + 1}`}
              title={name}
              image={{ src: imageUrl }}
              icons={[
                {
                  name: 'icon-calendar-16.svg',
                  text: formatIntlDate(begin),
                },
                { name: 'icon-pin-16.svg', text: location },
              ]}
              labelTag={getTranslations(liveUrl, ticketUrl).labelTag}
              linkOutText={getTranslations(liveUrl, ticketUrl).linkOut}
            />
          </DynamicLinkWrapper>
        ))}
      </div>
      {!isLastPage && (
        <Button
          iconPosition="left"
          icon={getDesignSystemIcon('icon-plus-16.svg')}
          onClick={onClickLoadMore}
          btnType="white_outline"
          className="m-auto mt-6"
          data-testid="loadMoreEvents"
        >
          {t('dashboard_events_button_loadMore')}
        </Button>
      )}
    </>
  ) : (
    <SharedHint className="mt-4" hint={t('dashboard_events_empty_paragraph')} dataTestId="events-empty-hint" />
  );
};
